import { Component, OnChanges, OnInit, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { SliderModule } from 'primeng/slider';
import { LocalizationService } from '../../../core/localization/localization.service';
import {
  WORKFLOW_AI_LOW_CONFIDENCE_POLICIES,
  WORKFLOW_AI_USE_CASE_OPTIONS,
  WorkflowAiOperatorConfig
} from '../workflow-catalog.models';
import { parseConfigJson, parseStringMapping, stringifyConfigJson } from './workflow-operator-config.utils';
import { WorkflowVariableMappingGridComponent } from './workflow-variable-mapping-grid.component';
import { WorkflowRetryPolicyFormComponent } from './workflow-retry-policy-form.component';

@Component({
  standalone: true,
  selector: 'ain-workflow-ai-operator-panel',
  imports: [
    FormsModule,
    SelectModule,
    InputTextModule,
    SliderModule,
    WorkflowVariableMappingGridComponent,
    WorkflowRetryPolicyFormComponent
  ],
  template: `
    <section class="operator-panel">
      <h5>{{ t('workflow.designer.operators.ai.title') }}</h5>

      <label>{{ t('workflow.designer.operators.ai.useCaseKey') }}</label>
      <p-select
        [options]="useCaseOptions"
        optionLabel="label"
        optionValue="value"
        [(ngModel)]="model.useCaseKey"
        (ngModelChange)="onUseCaseChanged()"
        [filter]="true"
        appendTo="body"
        [placeholder]="t('common.select')" />

      @if (selectedUseCase()?.outputSchema) {
        <label>{{ t('workflow.designer.operators.ai.outputSchema') }}</label>
        <input pInputText [ngModel]="selectedUseCase()?.outputSchema" readonly />
      }

      @if (operatorType() === 'AiAgent') {
        <label>{{ t('workflow.designer.operators.ai.promptRef') }}</label>
        <input pInputText [(ngModel)]="model.promptRef" (ngModelChange)="emitConfig()" maxlength="128" />
      }

      <label>{{ t('workflow.designer.operators.ai.minConfidence') }} ({{ confidencePercent }}%)</label>
      <p-slider [(ngModel)]="confidencePercent" [min]="0" [max]="100" (ngModelChange)="onConfidenceChanged()" />

      <label>{{ t('workflow.designer.operators.ai.onLowConfidence') }}</label>
      <p-select
        [options]="lowConfidenceOptions()"
        optionLabel="label"
        optionValue="value"
        [(ngModel)]="model.onLowConfidence"
        (ngModelChange)="emitConfig()"
        appendTo="body" />

      <h6>{{ t('workflow.designer.operators.inputMapping') }}</h6>
      <ain-workflow-variable-mapping-grid
        [mapping]="model.inputMapping ?? {}"
        (mappingChange)="onInputMappingChanged($event)" />

      <h6>{{ t('workflow.designer.operators.outputMapping') }}</h6>
      <ain-workflow-variable-mapping-grid
        [mapping]="model.outputMapping ?? {}"
        (mappingChange)="onOutputMappingChanged($event)" />

      <h6>{{ t('workflow.designer.operators.retry.title') }}</h6>
      <ain-workflow-retry-policy-form
        [retryPolicy]="model.retryPolicy"
        (retryPolicyChange)="onRetryPolicyChanged($event)" />
    </section>
  `,
  styles: [`
    .operator-panel { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; }
    .operator-panel h5, .operator-panel h6 { margin: 8px 0 4px; font-size: 13px; color: #334155; }
    .operator-panel label { font-size: 12px; color: #475569; }
  `]
})
export class WorkflowAiOperatorPanelComponent implements OnInit, OnChanges {
  private readonly localization = inject(LocalizationService);

  readonly operatorType = input<'AiAgent' | 'AiPrompt'>('AiAgent');
  readonly configJson = input('{}');
  readonly configJsonChange = output<string>();

  model: WorkflowAiOperatorConfig = { onLowConfidence: 'fail' };
  confidencePercent = 0;

  readonly useCaseOptions = WORKFLOW_AI_USE_CASE_OPTIONS.map(item => ({
    label: item.displayName,
    value: item.useCaseKey
  }));

  ngOnInit(): void {
    this.loadFromConfig(this.configJson());
  }

  ngOnChanges(): void {
    this.loadFromConfig(this.configJson());
  }

  lowConfidenceOptions() {
    return WORKFLOW_AI_LOW_CONFIDENCE_POLICIES.map(item => ({
      label: this.t(item.labelKey),
      value: item.value
    }));
  }

  selectedUseCase() {
    return WORKFLOW_AI_USE_CASE_OPTIONS.find(item => item.useCaseKey === this.model.useCaseKey);
  }

  onUseCaseChanged(): void {
    const useCase = this.selectedUseCase();
    if (useCase) {
      this.model.outputSchema = useCase.outputSchema;
      this.model.moduleKey = 'workflow-bpms';
    }
    this.emitConfig();
  }

  onConfidenceChanged(): void {
    this.model.minConfidence = this.confidencePercent / 100;
    this.emitConfig();
  }

  onInputMappingChanged(mapping: Record<string, string>): void {
    this.model.inputMapping = mapping;
    this.emitConfig();
  }

  onOutputMappingChanged(mapping: Record<string, string>): void {
    this.model.outputMapping = mapping;
    this.emitConfig();
  }

  onRetryPolicyChanged(retryPolicy: WorkflowAiOperatorConfig['retryPolicy']): void {
    this.model.retryPolicy = retryPolicy;
    this.emitConfig();
  }

  emitConfig(): void {
    this.configJsonChange.emit(stringifyConfigJson(this.model));
  }

  private loadFromConfig(raw: string): void {
    this.model = parseConfigJson<WorkflowAiOperatorConfig>(raw, {
      useCaseKey: '',
      moduleKey: 'workflow-bpms',
      onLowConfidence: 'fail',
      inputMapping: {},
      outputMapping: {}
    });
    this.model.inputMapping = parseStringMapping(this.model.inputMapping);
    this.model.outputMapping = parseStringMapping(this.model.outputMapping);
    this.confidencePercent = Math.round((this.model.minConfidence ?? 0) * 100);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
